/* eslint-disable max-lines-per-function */
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { setNameTextStyle, createDefaultWatermark } from '../../../helper/standardChart';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicRankingHorizontalBar',
  name_en: 'Dynamic Ranking Horizontal Bar',
  name_zh: '动态排名条形图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    y: amount => amount > 3,
    group: amount => amount > 1,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    group: { itemType: [DEFINE.DATE], category: DEFINE.SINGLE }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    group: string;
  };
  dvShowWaterMark?: boolean;
}

/** 暂不支持legend点击功能 */
export const dynamicRankingHorizontalBar = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  withTimeline: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const presetOption = hook.buildStandardChartPresetOption(api);
    const chartViewRect = $dom.getBoundingClientRect();
    const {
      dataIndex = 0,
      encoding: { x, y, group: groupBy },
      dvShowWaterMark = false
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * ------------------------- data
    let mappedNameList = new Set();
    let years = new Set();
    let formateDate = new Set();
    const dataset = data.values.map(item => {
      const year = +item[groupBy].replaceAll('-', '');
      mappedNameList.add(`${item[y]}`);
      years.add(year);
      formateDate.add(item[groupBy]);
      let value = +item[x];
      if (Number.isNaN(value)) {
        value = 0;
      }
      return {
        __origin: item,
        name: `${item[y]}`,
        value,
        date: year
      };
    });

    formateDate = Array.from(formateDate);
    mappedNameList = Array.from(mappedNameList);
    years = util.dataTransform.transform(Array.from(years), [{ type: 'sort', order: 'ascending' }]);

    // * ------------------------- chart
    const updateFrequency = 1200;
    const maxYear = years[years.length - 1];
    const colorList = presetOption?.color || [
      '#FF9500',
      '#14CCBD',
      '#1A9FFF',
      '#3366FF',
      '#4433FF',
      '#FF33AA',
      '#CC41D9',
      '#FFAD29',
      '#38D9C6',
      '#42B7FF',
      '#5C8AFF',
      '#6F5CFF',
      '#FF5CB6',
      '#DF6AE6',
      '#FFC252',
      '#60E6D2',
      '#6BCBFF',
      '#85ABFF',
      '#9785FF',
      '#FF85C4',
      '#F196F2',
      '#FFD57A',
      '#8DF2E0',
      '#94DDFF',
      '#ADCAFF',
      '#BCADFF',
      '#FFADD5',
      '#FFC7FE'
    ];

    // 配置项
    const legend = {
      data: mappedNameList.map((item, index) => ({
        name: item,
        color: colorList[index % colorList.length] || 'blue',
        type: util.const.SERIES_2_LEGEND_TYPE_MAP.$$common,
        selected: true,
        disableSelect: true
      })),
      show: false,
      selected: mappedNameList.reduce((pre, cur) => {
        pre[cur] = true;
        return pre;
      }, {})
    };
    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = {
      ...baseGridOption,
      top: 14
    };
    const yMaxNum = Math.floor((chartViewRect.height - grid.top - grid.bottom) / 15);

    const option = {
      legend,
      animationDuration: updateFrequency,
      animationDurationUpdate: updateFrequency,
      grid: [
        {
          ...grid
        }
      ],
      tooltip: {
        formatter: item => {
          const value = hook.formatDataNumberValue(item.value[0], {
            type: 'table',
            unit: dataMetaInfo[item.seriesName]?.unit
          });
          // prettier-ignore
          const itemObj = {
            type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
            color: item.color,
            name: item.name,
            value: value.value + value.unit
          }
          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: item?.axisValueLabel,
            items: [itemObj]
          });
        }
      },
      xAxis: [
        {
          show: true,
          axisLine: {
            show: true
          },
          axisTick: {
            show: false
          },
          position: 'top',
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          dvPadding: [75, 0],
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      yAxis: [
        {
          show: true,
          name: y,
          type: 'category',
          nameLocation: 'start',
          nameTextStyle: {
            verticalAlign: 'top',
            align: 'left',
            padding: [-20, 0, 0, 10]
          },
          max: Math.min(mappedNameList.length - 1, yMaxNum - 1),
          axisLine: {
            onZero: false,
            show: false
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            show: true,
            inside: true,
            margin: 0,
            align: 'left',
            formatter(value, index) {
              return `{rank${index > 2 ? '' : index + 1}|${index + 1}}  {name|${`${value}  `}}`;
            },
            rich: {
              name: {
                color: presetOption?.yAxis?.axisLabel?.color
              },
              rank: {
                color: presetOption?.yAxis?.axisLabel?.color,
                fontSize: 12,
                fontWeight: 500
              },
              rank1: {
                color: 'rgb(250, 59, 50)',
                fontSize: 12,
                fontWeight: 500
              },
              rank2: {
                color: 'rgb(255, 167, 66)',
                fontSize: 12,
                fontWeight: 500
              },
              rank3: {
                color: 'rgb(255, 214, 66)',
                fontSize: 12,
                fontWeight: 500
              }
            }
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      series: [
        {
          realtimeSort: true,
          type: 'bar',
          itemStyle: {
            color(param) {
              return colorList[mappedNameList.findIndex(n => param.name === n) % colorList.length];
            }
          },
          label: {
            fontWeight: 400,
            formatter(params) {
              const result = hook.formatDataNumberValue(params.value[0], {
                type: 'table',
                unit: dataMetaInfo[params.seriesName]?.unit
              });
              return `{value|${result.value + result.unit}}`;
            },
            rich: {
              value: {
                color: presetOption?.yAxis?.axisLabel?.color
              },
              n: {
                fontSize: 13
              },
              v: {
                fontSize: 12
              }
            }
          }
        }
      ],
      graphic: dvShowWaterMark
        ? createDefaultWatermark(chartViewRect, formateDate[formateDate.length - 1])
        : undefined
    };

    return renderApi => {
      const timelineIns = renderApi.renderTimelineProxy(formateDate as unknown as string[]);
      renderApi.renderLegendProxy(option.legend.data);
      setNameTextStyle($dom, option);
      const renderParams = { option: renderApi.mergeChartOptionProxy(option) };
      const chartIns = renderApi.renderChartProxy(renderParams, { theme: 'pc-wencai-light' });

      const helper = new ThsDataVStandardChart.helper.DynamicHistogramHelper(
        chartIns,
        renderParams.option,
        dataset,
        {
          watermarkFormatter(date) {
            const index = years.indexOf(date);
            return dvShowWaterMark ? formateDate[index] : '';
          }
        }
      );

      helper.play(maxYear, true);

      if (timelineIns instanceof api.ui.Dropdown) {
        timelineIns?.on?.('change', e => {
          helper.play(years[e.index]);
        });
      } else {
        timelineIns?.on?.('change', e => {
          // console.log('e: ', e);
          helper.play(years[e.index], e.causeBy !== 'axisClick' && e.causeBy !== 'axisMarkerDrag');
        });
      }
    };
  }
});
